/**
 * Packet-copy remux: extract a time range of a remote progressive mp4 into a
 * small standalone seekable mp4, WITHOUT re-encoding.
 *
 * Why not mediabunny's `Conversion`: any `trim.start > 0` forces a full
 * WebCodecs re-encode (`mediabunny/src/conversion.ts:1189` —
 * `needsTranscode = ... || firstTimestamp < this._startTimestamp`). We copy
 * encoded packets instead: no decode, no encode, no quality loss, and it runs
 * at roughly download speed. This mirrors mediabunny's own fast path
 * (`src/conversion.ts:1207-1245`) but starting from a non-zero packet.
 *
 * The cut is KEYFRAME-SNAPPED: output t=0 is the key packet at or before
 * `inSec`, so callers must apply the returned `startOffset`
 * (`el.currentTime = sourceTime - startOffset`).
 *
 * Most ways of getting this wrong produce a perfectly valid file that is
 * silently desynced or has corrupt trailing frames rather than throwing — the
 * comments below mark each of those.
 */
import {
  ALL_FORMATS,
  BufferTarget,
  EncodedAudioPacketSource,
  EncodedPacketSink,
  EncodedVideoPacketSource,
  Input,
  Mp4OutputFormat,
  Output,
  UrlSource,
  type EncodedPacket,
} from 'mediabunny';

/** H.264 allows at most 16 reorder frames, so a needed packet can never be
 *  more than this far behind in decode order. */
const REORDER_SLACK = 16;

export class RemuxCancelledError extends Error {
  constructor() {
    super('remux cancelled');
  }
}
/** The source cannot be packet-copied (codec, missing track, no keyframes). */
export class RemuxUnsupportedError extends Error {}
/** The requested range yielded no video packets (in-point past EOF). */
export class RemuxEmptyRangeError extends Error {}
/**
 * The clip is too long to remux in memory. Thrown from the metadata pre-pass,
 * before any media is fetched, so nothing is wasted.
 */
export class RemuxTooLargeError extends Error {}

/** Structurally compatible with exporter.ts's CancelToken (kept separate so
 *  this module has no DOM-dependent imports and can run under Node). */
export interface CancelToken {
  cancelled: boolean;
}

export interface RemuxResult {
  blob: Blob;
  /** Source-time of the output's t=0. ALWAYS <= inSec, by up to a full GOP. */
  startOffset: number;
  /** Actual span in the blob (>= outSec - startOffset, by the reorder slop). */
  duration: number;
  /**
   * True when the source ran out before `outSec` was reached — i.e. the blob
   * holds everything that exists from `startOffset` onwards.
   *
   * Callers MUST propagate this. A clip whose out-point exceeds the real media
   * duration (common: pan.do/ra metadata duration can overshoot the encoded
   * stream) would otherwise cache fine and then never satisfy a coverage check,
   * silently re-downloading on every attempt.
   */
  hitEnd: boolean;
  bytes: number;
  warnings: string[];
}

interface Prescan {
  bytes: number;
  count: number;
  minPts: number;
}

/**
 * Walk the range with `metadataOnly`, which reads byteLength straight out of
 * the already-parsed sample tables and fetches NO media bytes. One walk gives
 * three answers: size estimate (quota pre-check), packet count (progress) and
 * the rebase origin.
 */
async function prescan(
  sink: EncodedPacketSink,
  startTs: number,
  endTs: number,
  isVideo: boolean,
): Promise<Prescan | null> {
  const start = isVideo
    ? await sink.getKeyPacket(startTs, { metadataOnly: true })
    : await sink.getPacket(startTs, { metadataOnly: true });
  if (!start) return null;

  let bytes = 0;
  let count = 0;
  let minPts = Infinity;
  let idx = 0;
  // video only: bytes/packets seen but not yet known to be needed
  let heldBytes = 0;
  let heldCount = 0;

  for await (const p of sink.packets(start, undefined, { metadataOnly: true })) {
    // origin must be the MINIMUM pts near the head, not the key packet's own:
    // in an open GOP a trailing B-frame can sit below the IDR, and rebasing on
    // the IDR would push it negative (see the negative-timestamp trap below).
    if (idx < REORDER_SLACK) minPts = Math.min(minPts, p.timestamp);
    idx++;

    if (!isVideo) {
      if (p.timestamp >= endTs) break;
      bytes += p.byteLength;
      count++;
      continue;
    }
    heldBytes += p.byteLength;
    heldCount++;
    if (p.timestamp <= endTs) {
      bytes += heldBytes;
      count += heldCount;
      heldBytes = 0;
      heldCount = 0;
    } else if (heldCount > REORDER_SLACK) {
      break;
    }
  }
  return { bytes, count, minPts };
}

export async function remuxRange(
  url: string,
  inSec: number,
  outSec: number,
  opts: {
    cancel: CancelToken;
    onProgress?: (progress: number) => void;
    /** Refuse before fetching media if the estimate exceeds this. */
    maxBytes?: number;
  },
): Promise<RemuxResult> {
  const { cancel, onProgress, maxBytes } = opts;
  const warnings: string[] = [];
  const input = new Input({ source: new UrlSource(url), formats: ALL_FORMATS });
  let output: Output | null = null;

  try {
    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack) throw new RemuxUnsupportedError('source has no video track');
    if (!videoTrack.codec) throw new RemuxUnsupportedError('unrecognized video codec');
    const audioTrack = await input.getPrimaryAudioTrack();

    const vSink = new EncodedPacketSink(videoTrack);

    // Verified FIRST: verifyKeyPackets and metadataOnly are mutually exclusive
    // (media-sink.ts:104-106), so the prescan's lookup is unverified and we
    // feed it this timestamp to make sure both land on the same packet.
    // Verification matters: a lying `stss` means the output's first sample is
    // not a real IDR, which renders as a garbage first GOP with no error.
    let vKey = await vSink.getKeyPacket(inSec, { verifyKeyPackets: true });
    if (!vKey) vKey = await vSink.getFirstKeyPacket({ verifyKeyPackets: true });
    if (!vKey) throw new RemuxUnsupportedError('video track has no key packets');

    const vPre = await prescan(vSink, vKey.timestamp, outSec, true);
    if (!vPre || vPre.count === 0) throw new RemuxEmptyRangeError('no video packets in range');
    const origin = Math.min(vPre.minPts, vKey.timestamp);

    const aSink = audioTrack ? new EncodedPacketSink(audioTrack) : null;
    const aPre = aSink ? await prescan(aSink, origin, outSec, false) : null;

    const estimatedBytes = Math.round(
      (vPre.bytes + (aPre?.bytes ?? 0) + (vPre.count + (aPre?.count ?? 0)) * 16 + 4096) * 1.05,
    );
    // Checked BEFORE any media bytes are fetched (the pre-pass is metadata
    // only). The output is accumulated in memory until finalize, so an
    // oversized clip would otherwise climb to gigabytes and take the tab down
    // at ~90% — losing minutes of downloading with nothing to show.
    if (maxBytes !== undefined && estimatedBytes > maxBytes) {
      const mb = (n: number) => `${Math.round(n / 1048576)}MB`;
      throw new RemuxTooLargeError(
        `clip needs about ${mb(estimatedBytes)}, over the ${mb(maxBytes)} limit — split it into shorter clips and cache those`,
      );
    }

    // decoderConfig is asserted unconditionally on each track's first packet
    // (isobmff-muxer.ts:355/:459) — bail here rather than crash inside the muxer.
    const vDecoderConfig = await videoTrack.getDecoderConfig();
    if (!vDecoderConfig) throw new RemuxUnsupportedError('video decoder config unavailable');
    const vMeta = { decoderConfig: vDecoderConfig };

    output = new Output({ format: new Mp4OutputFormat(), target: new BufferTarget() });

    // Throws synchronously for an MP4-incompatible codec, before any media
    // bytes are fetched — cheapest check, so it goes first.
    let vSrc: EncodedVideoPacketSource;
    try {
      vSrc = new EncodedVideoPacketSource(videoTrack.codec);
      output.addVideoTrack(vSrc, { rotation: videoTrack.rotation });
    } catch (e) {
      throw new RemuxUnsupportedError(`video codec not mp4-compatible: ${String(e)}`);
    }

    // Audio is optional: resolve everything BEFORE addAudioTrack, so we never
    // end up with an added track we then decide to drop (which would ship an
    // audio track containing no samples).
    let aSrc: EncodedAudioPacketSource | null = null;
    let aMeta: { decoderConfig: AudioDecoderConfig } | null = null;
    if (audioTrack && aSink) {
      if (!audioTrack.codec) {
        warnings.push('audio dropped: unrecognized codec');
      } else {
        const cfg = await audioTrack.getDecoderConfig();
        if (!cfg) {
          warnings.push('audio dropped: decoder config unavailable');
        } else {
          try {
            const src = new EncodedAudioPacketSource(audioTrack.codec);
            output.addAudioTrack(src);
            aSrc = src;
            aMeta = { decoderConfig: cfg };
          } catch {
            warnings.push('audio dropped: codec not mp4-compatible');
          }
        }
      }
    }

    await output.start(); // must follow every addXTrack (output.ts:672-674)

    const totalPackets = vPre.count + (aPre?.count ?? 0);
    let done = 0;
    let reported = -1;
    const report = () => {
      if (!onProgress || totalPackets === 0) return;
      const step = Math.max(1, Math.floor(totalPackets / 20));
      if (done - reported < step) return;
      reported = done;
      onProgress(Math.min(1, done / totalPackets));
    };

    let videoEmitted = 0;
    let lastVideoEnd = origin;
    // Set only when we stop because the tail was found; if the iterator simply
    // runs out we reached end-of-media instead.
    let sawTail = false;

    const runVideo = async () => {
      // The emitted packets must be a CONTIGUOUS run in DECODE order. With
      // B-pyramids a packet with pts <= outSec can reference one with
      // pts > outSec, so filtering by pts corrupts the trailing frames
      // silently. Holding and flushing-on-demand guarantees contiguity: a
      // needed packet can only be emitted along with everything before it.
      //
      // Note this is deliberately NOT conversion.ts:1226's `pts >= end break`,
      // which is a presentation test on a decode-order iterator.
      const hold: EncodedPacket[] = [];
      for await (const p of vSink.packets(vKey, undefined, { verifyKeyPackets: true })) {
        if (cancel.cancelled) throw new RemuxCancelledError();
        hold.push(p);
        if (p.timestamp <= outSec) {
          for (const q of hold) {
            const ts = q.timestamp - origin;
            // The muxer rebases each track independently but only writes a
            // compensating edit list for POSITIVE offsets
            // (isobmff-boxes.ts:456). A negative rebased timestamp therefore
            // slides the whole track later with nothing to correct it — a
            // valid file that is silently out of sync. Never emit one.
            if (ts < 0) throw new Error(`negative rebased video timestamp: ${ts}`);
            await vSrc.add(q.clone({ timestamp: ts }), vMeta);
            videoEmitted++;
            done++;
            lastVideoEnd = Math.max(lastVideoEnd, q.timestamp + (q.duration ?? 0));
          }
          hold.length = 0;
          report();
        } else if (hold.length > REORDER_SLACK) {
          sawTail = true;
          break; // nothing needed for REORDER_SLACK packets → past the tail
        }
      }
      // Anything still held has pts > outSec and no kept dependents. Drop it —
      // flushing a partial hold is what would corrupt the tail.
    };

    const runAudio = async () => {
      if (!aSrc || !aSink || !aMeta) return;
      // AAC has no reordering and every packet is a key packet, so there is
      // nothing to snap. Take the packet at or after `origin`: the straddling
      // one would rebase negative (see above). Skipping it costs at most one
      // ~21ms frame, which the muxer renders as correctly-placed silence.
      //
      // Do NOT apply an AAC priming correction here — the demuxer already
      // applies the source's edit list (isobmff-demuxer.ts:949/:3110), so
      // correcting again double-corrects.
      let aStart = await aSink.getPacket(origin);
      if (!aStart) aStart = await aSink.getFirstPacket();
      else if (aStart.timestamp < origin) aStart = (await aSink.getNextPacket(aStart)) ?? aStart;
      if (!aStart) return;

      for await (const p of aSink.packets(aStart, undefined, {})) {
        if (cancel.cancelled) throw new RemuxCancelledError();
        // `<` not `<=`: a packet starting before outSec covers through it.
        if (p.timestamp >= outSec) break;
        const ts = p.timestamp - origin;
        if (ts < 0) continue; // pre-origin (priming) — not presented
        await aSrc.add(p.clone({ timestamp: ts }), aMeta);
        done++;
        report();
      }
    };

    // Concurrent so the mdat interleaves properly and UrlSource's two default
    // read workers each get a track. The muxer serializes adds internally.
    await Promise.all([runVideo(), runAudio()]);

    if (videoEmitted === 0) {
      await output.cancel().catch(() => undefined);
      throw new RemuxEmptyRangeError('no video packets emitted');
    }

    vSrc.close();
    aSrc?.close();
    await output.finalize();

    const buffer = (output.target as BufferTarget).buffer;
    if (!buffer) throw new Error('muxer produced no output');
    onProgress?.(1);
    return {
      blob: new Blob([buffer], { type: 'video/mp4' }),
      startOffset: origin,
      duration: lastVideoEnd - origin,
      hitEnd: !sawTail,
      bytes: buffer.byteLength,
      warnings,
    };
  } catch (e) {
    if (output && output.state === 'started') await output.cancel().catch(() => undefined);
    // input.dispose() in the finally aborts in-flight range requests, which
    // surfaces as InputDisposedError from the packet iterators. That is only
    // "cancellation" if we actually asked for it; otherwise it is a real bug.
    if (cancel.cancelled && !(e instanceof RemuxCancelledError)) throw new RemuxCancelledError();
    throw e;
  } finally {
    input.dispose();
  }
}
